import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ScreenFullDTO, VisitorTrackerDTO, VisitorTrackerScreenAssignmentDTO, VisitorTrackerScreenDTO } from 'src/app/services/api/api';
import { SankeyChartComponent, SbSankeyModel } from '@ngx-seatback/charts';
import { SbModalState } from '../../../../../services/gui/modal/modal-state.service';

@Component({
  selector: 'sb-comp-location-tracker-assignment-to-screens',
  templateUrl: './location-tracker-assignment-to-screens.component.html',
  styleUrls: ['./location-tracker-assignment-to-screens.component.scss'],
  standalone: false,
})
export class LocationTrackerAssignmentToScreensComponent implements OnInit {
  @ViewChild('sankey', { static: false }) sankey: SankeyChartComponent;

  @Input() isInModalContext: any;

  selectedData: { screens: ScreenFullDTO[]; tracker: VisitorTrackerDTO; assignments: VisitorTrackerScreenAssignmentDTO[] };
  trackerForm: UntypedFormGroup;
  sankeyData: SbSankeyModel[] = [];
  loading: boolean;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private modalState: SbModalState
  ) {
    this.loading = true;
    this.trackerForm = this.formBuilder.group({
      trackers: this.formBuilder.array([]),
    });
  }

  trackers(): UntypedFormArray {
    return this.trackerForm.get('trackers') as UntypedFormArray;
  }

  ngOnInit(): void {
    if (this.isInModalContext) {
      this.selectedData = this.modalState.options.get(this.isInModalContext).payload;
      if (this.selectedData) {
        this.loadAssignments();
      }
    }
  }

  loadAssignments() {
    for (const ass of this.selectedData.assignments) {
      this.addAssignment(ass);
    }
    this.convertToChartModel(this.selectedData).then();
  }

  addAssignmentByButton() {
    this.addAssignment();
  }

  addAssignment(ass?: any) {
    const assignment = this.formBuilder.group({
      tracker: new UntypedFormControl(ass ? ass.tracker : ''),
      screen: new UntypedFormControl(ass ? this.resolveScreen(ass.screen) : ''),
      value: new UntypedFormControl(ass ? ass.amount : ''),
    });
    this.trackers().push(assignment);
  }

  deleteRow(i: number) {
    this.loading = true;
    const delScreenId = this.trackerForm.get(['trackers', i, 'screen']).value.id;

    this.removeAssignment(delScreenId).then(() => {
      this.trackers().removeAt(i);
      this.sankey.updateData(this.sankeyData);
    });
  }

  removeAssignment(delScreenId: number) {
    return new Promise(resolve => {
      if (delScreenId) {
        for (const [index, assign] of this.sankeyData.entries()) {
          if (assign.to.toString() === this.attachPrefix(delScreenId)) {
            this.sankeyData.splice(index, 1);
            if (this.sankeyData.length === 0) {
              this.sankeyData.push({
                from: this.attachPrefix(this.selectedData.tracker.id),
                to: undefined,
                value: undefined,
                additionalData: {
                  from: this.selectedData.tracker,
                  to: { _type: 'ScreenFullDTO' },
                },
              });
            }
            break;
          }
        }
      }
      resolve(true);
    });
  }

  convertToChartModel(data) {
    return new Promise(resolve => {
      this.sankeyData = [];
      this.loading = true;
      if (data?.assignments && data?.assignments?.length > 0) {
        for (const ass of data.assignments) {
          this.sankeyData.push({
            from: this.attachPrefix(ass.tracker),
            to: this.attachPrefix(ass.screen),
            value: ass.amount,
            additionalData: {
              from: this.selectedData.tracker,
              to: this.resolveScreen(ass.screen),
            },
          });
        }
      } else if (data?.tracker) {
        this.sankeyData.push({
          from: this.attachPrefix(this.selectedData.tracker.id),
          to: undefined,
          value: undefined,
          additionalData: {
            from: this.selectedData.tracker,
            to: { _type: 'ScreenFullDTO' },
          },
        });
      }
      this.loading = false;
      resolve(true);
    });
  }

  save() {
    const savedData: VisitorTrackerScreenDTO[] = [];
    const assignments = this.trackers().controls;
    for (let i = 0; i < assignments.length; i++) {
      const assignment: VisitorTrackerScreenDTO = {
        visitorTracker: this.selectedData.tracker,
        screen: this.trackerForm.get(['trackers', i, 'screen']).value,
        amount: Number(this.trackerForm.get(['trackers', i, 'value']).value),
      };
      savedData.push(assignment);
    }
    this.modalState.modals.get(this.isInModalContext).close(savedData);
  }

  cancel() {
    this.modalState.modals.get(this.isInModalContext).close(null);
  }

  updateValue(i: number) {
    const to = this.trackerForm.get(['trackers', i, 'screen']).value.id;
    let index: number = -1;
    for (const [idx, singleSankey] of this.sankeyData.entries()) {
      if (singleSankey.to === this.attachPrefix(to)) {
        index = idx;
      }
    }
    if (index === -1) {
      index = 0;
    }
    if (i >= 0 && i <= this.sankeyData.length - 1) {
      this.sankeyData[index].to = this.attachPrefix(this.trackerForm.get(['trackers', i, 'screen']).value.id);
      this.sankeyData[index].additionalData.to = this.trackerForm.get(['trackers', i, 'screen']).value;
      this.sankeyData[index].value = Number(this.trackerForm.get(['trackers', i, 'value']).value);
      this.sankey.updateData(this.sankeyData);
    } else {
      this.sankeyData.push({
        from: this.attachPrefix(this.selectedData.tracker.id),
        to: this.attachPrefix(this.trackerForm.get(['trackers', i, 'screen']).value.id),
        value: Number(this.trackerForm.get(['trackers', i, 'value']).value),
        additionalData: {
          from: this.selectedData.tracker,
          to: this.trackerForm.get(['trackers', i, 'screen']).value,
        },
      });
    }
  }

  resolveScreen(screenId: number) {
    if (this.selectedData?.screens) {
      for (const screen of this.selectedData.screens) {
        if (screen.id === screenId) {
          return screen;
        }
      }
      return null;
    }
  }

  isDisplayed(screen: ScreenFullDTO) {
    for (const ass of this.trackers().controls) {
      if (ass?.value?.screen?.id?.toString() === screen?.id?.toString()) {
        return true;
      }
    }
    return false;
  }

  attachPrefix(element: number): string {
    return 'ID: ' + element.toString();
  }
}
